import { ParseError, parsePhoneNumber } from "libphonenumber-js/max";
import { LandCode } from "../../common/defaultValues";
import { PropValidator } from "../../common/entityStore";
import { getInvalidValueText, getRecommendedFieldText, propInvalidCharacter, propLength, propLengthMax, propLengthMin, propNotRegex, propOnlyDigits, propRegex, propRequired, propValidEmail, propValueValidator } from "../../common/entityStoreValidators";
import { CustomerSexTypeId } from "../../common/enum";
import { getValidationText } from "../../common/localization";
import { KeysOfTypeGet, landCodes } from "../../common/utils";
import { CustomerAddressBaseDto } from "./types";
import removeAccents from "remove-accents";

function getText(key: string, defaultMessage: string) {
    return getValidationText("Customer." + key, defaultMessage);
}

export function getTelMobValidators<T extends { mob: string; tel: string; }>(landCode: LandCode | undefined, prop: KeysOfTypeGet<T, string | null | undefined>): (PropValidator<T> | false)[] {
    return [
        ...getPhoneFieldValidators(landCode, prop),
        prop === "mob" && propValueValidator(prop, p => typeof p === "string" && p && !isMobile(p, landCode) && getInvalidValueText()),
        prop === "tel" && propValueValidator(prop, (p, e) => typeof p === "string" && p && !isLandline(p, landCode) && !(isMobile(p, landCode) && isMobile(e.mob, landCode) && p?.trim() !== e.mob?.trim()) && getInvalidValueText()),
    ];
}

export function getPhoneFieldValidators<T extends {}>(landCode: LandCode | undefined, prop: KeysOfTypeGet<T, string | null | undefined>): (PropValidator<T> | false)[] {
    return [
        propOnlyDigits(prop),
        landCodes("CZ", "SK", "SI", "PL").includes(landCode) && propLength(prop, 9, 9),
        landCodes("HR").includes(landCode) && propLength(prop, 9, 10),
        landCodes("EE", "HU").includes(landCode) && propLength(prop, 10, 11),
        landCodes("LV").includes(landCode) && propLength(prop, 11, 11),
        landCodes("CZ", "SK").includes(landCode) && propRegex(prop, /^0/, getText("Phone.DoesNotStartWith0", "Phone number must not start with 0.")),
        landCodes("SI", "HR").includes(landCode) && propNotRegex(prop, /^0/, getText("Phone.StartstWith0", "Phone number must start with 0.")),
        landCode === "EE" && propNotRegex(prop, /^372/, getText("Phone.StartstWith372", "Phone number must start with 372.")),
        landCode === "LV" && propNotRegex(prop, /^371/, getText("Phone.StartstWith371", "Phone number must start with 371.")),
        landCode === "HU" && propNotRegex(prop, /^36/, getText("Phone.StartstWith36", "Phone number must start with 36.")),
    ];
}

export function isMobile(value: string, landCode: LandCode | undefined) {
    try {
        const type = parsePhoneNumber(value, landCode).getType();
        return type === "FIXED_LINE_OR_MOBILE" || type === "MOBILE";
    } catch (error) {
        if (error instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
        } else {
            throw error;
        }
    }
}
function isLandline(value: string, landCode: LandCode | undefined) {
    try {
        const type = parsePhoneNumber(value, landCode).getType();
        return type === "FIXED_LINE_OR_MOBILE" || type === "FIXED_LINE";
    } catch (error) {
        if (error instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
        } else {
            throw error;
        }
    }
}

function isValidSkZipCode(p: string) {
    return (p >= "01000" && p <= "09599") || (p >= "81000" && p <= "85999") || (p >= "90000" && p <= "99999");
}

function isNameValid(name: string) {
    return /^[a-zA-Z-(). ]*$/.test(removeAccents(name));
}

export function getCustomerAddressBaseValidators(landCode: LandCode, isTm: boolean, isDpdDefault: any): (PropValidator<CustomerAddressBaseDto> | null | false)[] {
    return [
        propRequired("customerSexTypeId", undefined, p => p != null && p > CustomerSexTypeId.Unknown),
        propRequired("fname"),
        propRequired("lname"),
        propLengthMin("lname", 2),
        propValueValidator("fname", p => !isNameValid(p) && getInvalidValueText()),
        propValueValidator("lname", p => !isNameValid(p) && getInvalidValueText()),

        propRequired("zipCode"),
        propOnlyDigits("zipCode"),
        landCodes("CZ", "SK", "EE", "HR", "PL").includes(landCode) && propLength("zipCode", 5, 5),
        landCodes("SI", "LV", "HU").includes(landCode) && propLength("zipCode", 4, 4),
        landCode === "CZ" && propRegex("zipCode", /^[089]/, getInvalidValueText()),
        landCode === "SK" && propValueValidator("zipCode", p => p && !isValidSkZipCode(p) && getInvalidValueText()),
        landCode === "SI" && propRegex("zipCode", /^[07]/, getInvalidValueText()),
        landCode === "HR" && propNotRegex("zipCode", /^(10[0-4]|2[0-3]|3[1-5]|4[02-57-9]|5[1-3])/, getInvalidValueText()),

        propRequired("city"),
        propLengthMin("city", 2),
        propRegex("city", /^([0-9]|.[0-9])/, getText("NoDigitInFirstTwoCharacters", "Field must not contain digit in first two characters.")),

        ...getTelMobValidators<CustomerAddressBaseDto>(landCode, "tel"),
        ...getTelMobValidators<CustomerAddressBaseDto>(landCode, "mob"),
        isTm && propValueValidator("tel", (p, e) => !p && !e.mob && getText("Phone.MustBeSpecified", "Phone or mobile must be specified.")),
        isTm && propValueValidator("mob", (p, e) => !p && !e.tel && getText("Phone.MustBeSpecified", "Phone or mobile must be specified.")),
        !isTm && propValueValidator("tel", (p, e) => !p && !e.mob && { message: "Phone or mobile is recommended.", type: "recommended" }),
        !isTm && propValueValidator("mob", (p, e) => !p && !e.tel && { message: "Phone or mobile is recommended.", type: "recommended" }),
        isDpdDefault && propValueValidator("fname", (p, e) => p.length + e.lname.length > 35 && { message: getText("FirstName.DPDRecommended", "Max length for firstname and last name combined is 35 characters."), type: "recommended" }),
        isDpdDefault && propValueValidator("lname", (p, e) => p.length + e.fname.length > 35 && { message: getText("LastName.DPDRecommended", "Max length for firstname and last name combined is 35 characters."), type: "recommended" }),
        isDpdDefault && propValueValidator("strt1", (p, e) => p.length + e.strt2.length > 43 && { message: getText("Street.DPDRecommended", "Max length for street and house number is 43 characters. Use CoAdr field for any details"), type: "recommended" }),
        isDpdDefault && propValueValidator("strt2", (p, e) => p.length + e.strt1.length > 43 && { message: getText("HouseNo.DPDRecommended", "Max length for street and house number is 43 characters. Use CoAdr field for any details"), type: "recommended" }),
        isDpdDefault && propValueValidator("city", (p) => p.length > 35 && { message: getText("City.DPDRecommended", "Max length is 35 characters. Use CoAdr field for any details"), type: "recommended" }),
        isDpdDefault && propValueValidator("coadr", (p) => p.length > 35 && { message: getText("CoAdr.DPDRecommended", "Max length is 35 characters for DPD to process."), type: "recommended" }),
        isDpdDefault && propValueValidator("email", (p) => p.length > 50 && { message: getText("Email.DPDRecommended", "Max length is 50 characters for DPD to process."), type: "recommended" }),

        propValidEmail("email"),

        propInvalidCharacter("title", ";"),
        propInvalidCharacter("fname", ";"),
        propInvalidCharacter("lname", ";"),
        propInvalidCharacter("coadr", ";"),
        propInvalidCharacter("strt1", ";"),
        propInvalidCharacter("strt2", ";"),
        propInvalidCharacter("city", ";"),

        propLengthMax("title", 50),
        propLengthMax("fname", 50),
        propLengthMax("lname", 50),
        propLengthMax("coadr", 50),
        propLengthMax("strt1", 50),
        propLengthMax("strt2", 50),
        propLengthMax("city", 50),
        propLengthMax("email", 50),

        propRequired("strt1"),
        propRequired("strt2", { message: getRecommendedFieldText(), type: "recommended" }),
        propRequired("email", { message: getRecommendedFieldText(), type: "recommended" }),
    ];
}
